import React, { createContext, useContext, useState, useEffect } from "react";

const EditorPreferencesContext = createContext();

const STORAGE_KEY = "editorPreferences";

const defaultPreferences = {
  showIconLabels: true,
};

const loadPersistedPreferences = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    console.warn("Failed to load persisted editor preferences:", error);
    return {};
  }
};

/**
 * Provider for managing editor preferences with localStorage persistence
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export const EditorPreferencesProvider = ({ children }) => {
  // Load persisted preferences and merge with defaults
  const persisted = loadPersistedPreferences();
  const [preferences, setPreferences] = useState({
    ...defaultPreferences,
    ...persisted,
  });

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.warn("Failed to save editor preferences:", error);
    }
  }, [preferences]);

  const updatePreference = (key, value) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetPreferences = () => {
    setPreferences(defaultPreferences);
  };

  const contextValue = {
    preferences,
    updatePreference,
    resetPreferences,
    // Convenience getters
    showIconLabels: preferences.showIconLabels,
  };

  return (
    <EditorPreferencesContext.Provider value={contextValue}>
      {children}
    </EditorPreferencesContext.Provider>
  );
};

/**
 * Hook for consuming editor preferences
 * @returns {Object} Editor preferences context
 */
export const useEditorPreferences = () => {
  const context = useContext(EditorPreferencesContext);
  if (!context) {
    throw new Error(
      "useEditorPreferences must be used within EditorPreferencesProvider"
    );
  }
  return context;
};
